// How many switch reads a page has in flight at once (#1786, ZENITH.md §9.5,
// ADR-0045 decision 5). Every switch of the site is read; `state_fan_out`,
// which the links answer carries, is how many of those reads run at the same
// time, and the rest wait their turn. It is the server's
// `ZENITH_VALUE_STATE_FAN_OUT`, the figure the server's own comparison read
// uses as a semaphore over every switch of the site (`observeSwitches`), and
// this page holds no number of its own.
//
// It used to be read as a count: the first `state_fan_out` switches by name
// were read and every switch after them was labelled *not read*, so a site
// with more switches than the figure was part unread on every page that
// draws one.
import { useMemo } from "react";

/** A queue of reads, at most `limit` of them started and not yet answered. */
export interface FanOut {
  /** Starts `read` when a slot is free, in the order the reads were asked
   * for. `live` is asked when the slot comes: a read whose asker has gone by
   * then is not started, and its promise is rejected. */
  run<T>(read: () => Promise<T>, live?: () => boolean): Promise<T>;
}

/** A fan-out of at most `limit` reads at once; never less than one, so a
 * figure the page has not been told yet cannot stop every read. */
export function fanOut(limit: number): FanOut {
  const most = Math.max(1, limit);
  let active = 0;
  const waiting: (() => void)[] = [];
  const next = () => {
    while (active < most && waiting.length > 0) waiting.shift()!();
  };
  return {
    run<T>(read: () => Promise<T>, live: () => boolean = () => true) {
      return new Promise<T>((resolve, reject) => {
        waiting.push(() => {
          if (!live()) { reject(new Error("the read was no longer wanted")); return; }
          active++;
          let started: Promise<T>;
          try { started = read(); } catch (e) { started = Promise.reject(e); }
          started.then(resolve, reject).finally(() => { active--; next(); });
        });
        next();
      });
    },
  };
}

/** The page's one fan-out over its switch reads, sized by the server's
 * `state_fan_out`. One per page: every `SwitchRead` the page renders is handed
 * the same one, so the bound is the page's and not each switch's. */
export function useFanOut(bound: number | undefined): FanOut {
  return useMemo(() => fanOut(bound ?? 1), [bound]);
}
